import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import useFormSubmit from "../Form/useFormSubmit";
import Form, { FORM_HORIZONTAL_LAYOUT } from "../Form";
import { reloadTable } from "../SmartTable/reducer";
import { closeModalAction } from "../Modal/reducer";
import Button from "../Button";
import { renderDynamicallyFields } from "../Form/methods";
import { FORM_ITEM_TEXT } from "../Form/FormItems/FormItemText";
import { FORM_ITEM_RESOURCE_SELECT } from "../Form/FormItems/FormItemResourceSelect";
import { countries } from "../../pages/Organization/OrgSettings/Branches/config";
import { FORM_ITEM_TEXT_AREA } from "../Form/FormItems/FormItemTextArea";
import { FORM_TYPE_UPLOAD_IMAGE } from "../Form/FormItems/FormItemUploadImage";
import { FORM_ITEM_CHECKBOX } from "../Form/FormItems/FormItemCheckbox";
import { formatPhoneNumber } from "../../utils/common";

/**
 * Shared EntityForm component for QAM and DOM entities.
 *
 * @param {object} props
 * @param {string} props.name - Optional modal name override.
 * @param {string} props.defaultModalName - The default modal name constant.
 * @param {string} props.tableName - The table name for reload action.
 */
const EntityForm = ({ name, defaultModalName, tableName }) => {
  const data = useSelector((state) => state?.modal[name || defaultModalName]?.data) || {};
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isNewRecord = !data.id;
  const { loading, submit, generalError } = useFormSubmit({
    route: isNewRecord ? "qam/entities" : `qam/entities/${data.id}`,
    method: isNewRecord ? "post" : "put",
    form,
    onSuccess: () => {
      data.onSuccess?.();
      dispatch(reloadTable({ name: tableName, resetFilters: isNewRecord }));
      dispatch(closeModalAction({ name: name || defaultModalName }));
    }
  });

  return (
    <Form
      onSubmit={submit}
      form={form}
      errors={generalError}
      loading={loading}
      initialValues={{
        active: true,
        ...data,
        images:
          data.files?.length > 0
            ? {
                src: data.files[0].sizes.users.md,
                name: data.files[0].name,
                id: data.files[0].id
              }
            : {}
      }}
      layout={FORM_HORIZONTAL_LAYOUT}
      header={
        <>
          <div className="h3">{t(isNewRecord ? "qam.new_entity.form_title" : "qam.edit_entity.form_title")}</div>
          <div className="mb-30 mt-10 h6 font-weight-300 text-muted">{t("container.general_words.form_subtitle")}</div>
        </>
      }
    >
      {renderDynamicallyFields([
        {
          type: FORM_ITEM_TEXT,
          max: 200,
          name: "number",
          label: "qam.new_entity.number",
          placeholder: "qam.new_entity.number"
        },
        {
          required: true,
          type: FORM_ITEM_TEXT,
          max: 200,
          name: "name",
          label: "qam.new_entity.company_name",
          placeholder: "qam.new_entity.company_name"
        },
        {
          type: FORM_ITEM_TEXT,
          max: 200,
          name: "address",
          label: "qam.new_entity.address1",
          placeholder: "qam.new_entity.address1"
        },
        {
          type: FORM_ITEM_TEXT,
          max: 200,
          name: "address2",
          label: "qam.new_entity.address2",
          placeholder: "qam.new_entity.address2"
        },
        {
          type: FORM_ITEM_TEXT,
          max: 200,
          name: "zip",
          label: "qam.new_entity.zip",
          placeholder: "qam.new_entity.zip"
        },
        {
          type: FORM_ITEM_TEXT,
          max: 200,
          name: "city",
          label: "qam.new_entity.city",
          placeholder: "qam.new_entity.city"
        },
        {
          type: FORM_ITEM_RESOURCE_SELECT,
          name: "country",
          label: "qam.new_entity.country",
          placeholder: "qam.new_entity.country_placeholder",
          options: countries.map((item) => ({
            value: item.name,
            text: item.name
          }))
        },
        {
          type: FORM_ITEM_TEXT,
          max: 200,
          name: "phone",
          label: "qam.new_entity.phone",
          placeholder: "qam.new_entity.phone",
          normalize: (value) => formatPhoneNumber(value)
        },
        {
          type: FORM_ITEM_TEXT,
          email: true,
          max: 200,
          name: "email",
          label: "container.general_words.email",
          placeholder: "container.general_words.email"
        },
        {
          type: FORM_ITEM_TEXT,
          max: 200,
          name: "url",
          label: "qam.new_entity.website_url",
          placeholder: "qam.new_entity.website_url"
        },
        {
          type: FORM_ITEM_TEXT_AREA,
          name: "remarks",
          label: "qam.new_entity.internal_remarks",
          placeholder: "qam.new_entity.internal_remarks",
          autoSize: { minRows: 2, maxRows: 6 }
        },
        {
          type: FORM_TYPE_UPLOAD_IMAGE,
          name: "images",
          label: "qam.new_entity.picture",
          placeholder: "qam.new_entity.picture",
          fieldProps: {
            purpose: "picture",
            entity: "entity",
            type: "image",
            cardImageStyle: { width: 80, height: 80 }
          }
        },
        {
          type: FORM_ITEM_CHECKBOX,
          visible: isNewRecord,
          name: "active",
          label: "container.qam_settings.scopes_tab.active"
        }
      ])}
      <div className="flex mt-25 flex-justify-end">
        <Button
          className="min-w-150"
          type="primary"
          htmlType="submit"
          disabled={loading}
          loading={loading}
          icon={<i className="ti-check" />}
        >
          {t("container.general_words.save")}
        </Button>
      </div>
    </Form>
  );
};

export default EntityForm;
